import { dateFormat, formatDate, type DateFormat } from './dates';

/**
 * Date boxes written the organisation's way.
 *
 * A native `<input type="date">` is the browser's control and is written in
 * the browser's language — Chrome installed in English (US) shows mm/dd/yyyy
 * in every one of them whatever the organisation has chosen, on a screen where
 * every date beside it reads dd/mm/yyyy. The two conventions a box apart is
 * exactly the confusion the setting exists to end.
 *
 * So every date box is wrapped. The native input stays — it keeps its name,
 * its id and its ISO value, so every form that reads it goes on reading it
 * unchanged, and it still supplies the calendar picker — but it is hidden, and
 * a text box in front of it shows and takes the date in the organisation's
 * convention. Typing into the text box writes the native one; picking from the
 * calendar writes the text box.
 *
 * Done once, here, for the whole application: a `MutationObserver` on the body
 * enhances every date box as it appears, so a form that adds one does nothing
 * special and cannot forget to. Seventy-seven of them across twenty-five files
 * was the count when this was written, which is the argument for an observer
 * over a helper each of them has to remember to call.
 */

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** What an empty box says is wanted. */
export const datePlaceholder = (format: DateFormat = dateFormat()): string =>
  format === 'DD_MM_YYYY' ? 'dd/mm/yyyy' : format === 'MM_DD_YYYY' ? 'mm/dd/yyyy' : 'd mmm yyyy';

const two = (n: number): string => String(n).padStart(2, '0');

/** The calendar day exists. `2026-02-31` does not. */
const valid = (y: number, m: number, d: number): boolean => {
  if (!(y >= 1900 && y <= 2200 && m >= 1 && m <= 12 && d >= 1 && d <= 31)) return false;
  const probe = new Date(Date.UTC(y, m - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
};

/**
 * A typed date to ISO `yyyy-mm-dd`, or null for anything that is not one.
 *
 * Reads the organisation's convention, and ISO always — a date pasted from
 * elsewhere in the application is ISO, and refusing it would be perverse. Under
 * the default (the month as a word) a purely numeric date is read day-first,
 * because that is the convention of everywhere this application is used that
 * has not chosen otherwise; the placeholder says so.
 *
 * A two-digit year is this century. `31/02/2026` is refused rather than rolled
 * over into March, because a date nobody typed is worse than a box that stays
 * red.
 */
export function parseDate(raw: string, format: DateFormat = dateFormat()): string | null {
  const text = raw.trim();
  if (!text) return null;

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
  if (iso) {
    const [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
    return valid(y, m, d) ? `${y}-${two(m)}-${two(d)}` : null;
  }

  // 21 Aug 2026, 21 aug 26, 21 August 2026
  const worded = /^(\d{1,2})\s+([A-Za-z]{3,})\.?\s+(\d{2}|\d{4})$/.exec(text);
  if (worded) {
    const m = MONTHS.indexOf(worded[2].slice(0, 3).toLowerCase()) + 1;
    const d = Number(worded[1]);
    const y = worded[3].length === 2 ? 2000 + Number(worded[3]) : Number(worded[3]);
    return m && valid(y, m, d) ? `${y}-${two(m)}-${two(d)}` : null;
  }

  // 21/08/2026, 21.08.2026, 21-08-26 — in the order the convention says.
  const numeric = /^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{2}|\d{4})$/.exec(text);
  if (numeric) {
    const a = Number(numeric[1]);
    const b = Number(numeric[2]);
    const y = numeric[3].length === 2 ? 2000 + Number(numeric[3]) : Number(numeric[3]);
    const [d, m] = format === 'MM_DD_YYYY' ? [b, a] : [a, b];
    return valid(y, m, d) ? `${y}-${two(m)}-${two(d)}` : null;
  }

  return null;
}

/** ISO to the organisation's convention, or empty. */
const display = (iso: string): string => (iso ? formatDate(new Date(`${iso}T00:00:00.000Z`), dateFormat()) : '');

const CALENDAR = '<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><rect x="1.5" y="3" width="13" height="11.5" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M1.5 6.5h13M5 1.5v3M11 1.5v3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';

/**
 * Wrap one native date box.
 *
 * Idempotent: a box already wrapped is left alone, which is what lets the
 * observer fire on every mutation without care.
 */
export function enhanceDateInput(native: HTMLInputElement): void {
  if (native.dataset.dateEnhanced) return;
  native.dataset.dateEnhanced = '1';

  const wrap = document.createElement('span');
  wrap.className = 'datein';

  const text = document.createElement('input');
  text.type = 'text';
  text.inputMode = 'numeric';
  text.autocomplete = 'off';
  text.placeholder = datePlaceholder();
  text.value = display(native.value);
  text.disabled = native.disabled;
  text.readOnly = native.readOnly;
  text.className = native.className;
  if (native.style.cssText) text.style.cssText = native.style.cssText;
  text.setAttribute('aria-label', native.getAttribute('aria-label') ?? 'Date');

  const pick = document.createElement('button');
  pick.type = 'button';
  pick.className = 'datein-pick';
  pick.setAttribute('aria-label', 'Pick from a calendar');
  pick.innerHTML = CALENDAR;
  pick.disabled = native.disabled;

  native.parentNode?.insertBefore(wrap, native);
  wrap.append(text, pick, native);
  native.classList.add('datein-native');
  native.tabIndex = -1;
  native.setAttribute('aria-hidden', 'true');

  /**
   * Typing writes through as soon as the text is a date, and `change` is
   * fired on the native box so anything listening there — a form that
   * recomputes when a date moves — hears it exactly as before.
   */
  const commit = () => {
    const raw = text.value;
    if (!raw.trim()) {
      wrap.classList.remove('bad');
      if (native.value) {
        native.value = '';
        native.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }
    const iso = parseDate(raw);
    wrap.classList.toggle('bad', iso === null);
    // A box that does not read as a date carries no date: the previous valid
    // one is not left behind to be submitted under a red box.
    const next = iso ?? '';
    if (next !== native.value) {
      native.value = next;
      native.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
  text.addEventListener('input', commit);
  // On leaving the box a valid date is rewritten tidily — `21/8/26` becomes
  // `21/08/2026` — so what is on screen is what the record holds.
  text.addEventListener('blur', () => {
    commit();
    if (native.value && !wrap.classList.contains('bad')) text.value = display(native.value);
  });

  // The calendar writes the text box; nothing else does.
  native.addEventListener('change', () => {
    const shown = parseDate(text.value);
    if (shown !== (native.value || null)) {
      text.value = display(native.value);
      wrap.classList.remove('bad');
    }
  });

  pick.addEventListener('click', () => {
    try {
      // Chrome, Edge, Firefox. Needs a user gesture, which this is.
      (native as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
    } catch {
      native.focus();
    }
  });
}

export const enhanceDateInputs = (root: ParentNode): void => {
  root.querySelectorAll<HTMLInputElement>('input[type="date"]').forEach(enhanceDateInput);
};

/**
 * Watch the whole document, once.
 *
 * Dialogs are appended to the body; screens are drawn into #app; a dialog that
 * refreshes rewrites its own body. All of it is a mutation, and every date box
 * any of it produces is wrapped before the next paint.
 */
export function watchDateInputs(): void {
  enhanceDateInputs(document);
  new MutationObserver((records) => {
    for (const r of records) {
      r.addedNodes.forEach((n) => {
        if (!(n instanceof Element)) return;
        if (n instanceof HTMLInputElement && n.type === 'date') enhanceDateInput(n);
        else enhanceDateInputs(n);
      });
    }
  }).observe(document.body, { childList: true, subtree: true });
}
